/**
 * The GA4 shopping events, for every V2 page that sells something.
 *
 * `view_item`, `view_item_list`, `add_to_cart` and `remove_from_cart`, built from the
 * types the storefront api already hands a page — `Product` and `CartItem` — so a page
 * adds an event by naming what happened rather than by assembling a payload. The field
 * names and the item shape are the legacy `modules/analytics4.js`'s, which is what makes
 * a V2 event and its legacy neighbours land in one report.
 *
 * Everything goes out through `trackEvent`, which holds it until the visitor's context is
 * in the dataLayer — see "V2 analytics events" in docs/gtm.md. Nothing here touches
 * `window.gtag` itself.
 *
 * Two rules this module exists to keep in one place:
 *
 * 1. **`item_id` is the base sku's code** — see `itemId`. Never the numeric product id,
 *    and never the sku the customer actually bought.
 * 2. **`add_to_cart` carries `value`.** The live "Pinterest - Block Add to Cart when no
 *    order value" trigger reads it and drops the event as a free item when it is missing
 *    or zero.
 */

import { Product, CartItem } from '../types';
import { round2 } from '../utils';
import { trackEvent } from './dispatcher';

const CURRENCY = 'USD';

/**
 * How long impressions are gathered before they go out as one event.
 *
 * Every tile reports itself, so they arrive one callback at a time, and in a single
 * column they arrive one *frame* at a time as well. Without a window wide enough to cover
 * a scroll coming to rest, a visitor working down a several-hundred-product sale would
 * send a `view_item_list` per tile: hundreds of events, each with one item, each running
 * every GTM tag bound to the event, during the scroll.
 */
const BATCH_MS = 300;

/**
 * GA4 keeps the first 200 items of an event and silently discards the rest, so a batch
 * that large is split rather than truncated.
 */
const MAX_ITEMS_PER_EVENT = 200;

/** A GA4 item, in the shape `modules/analytics4.js` has always sent. */
export type AnalyticsItem = {
  id: string;
  item_id: string;
  item_name: string;
  currency: string;
  index: number;
  price?: number;
  quantity?: number;
  coupon?: string | null;
  item_category?: string;
};

/**
 * What a page has to know about a product to report it, whatever payload it was handed.
 *
 * `Product` satisfies this through `productItem` below; a page fed by its own document —
 * the mega sale reads `blackfriday.json`, not the api — fills it in itself. Deliberately
 * not a second product type: it is the argument list of one function, and nothing holds
 * one of these.
 */
type ItemInput = {
  /** The numeric product id, used only as the fallback id. See `itemId`. */
  productId: number;
  /** The base sku's code, when the payload carries one. */
  baseSkuCode?: string | null;
  name: string;
  /** What the page is showing for this product, already discounted. */
  price?: number;
  /** Position in the list. Left out where the caller does not know it — see `reportImpression`. */
  index?: number;
  quantity?: number;
  coupon?: string | null;
  category?: string;
};

/**
 * The id a product is reported under: its base sku's code.
 *
 * This is `tSys::trackingProductId()`, in the browser — including its fallback. Legacy
 * hands that function a product row and gets back `baseSku->getCode()`, or the numeric id
 * as a string when the product has no base sku (no active sku whose term and type carry
 * no price adjustment). Every payload here can be missing the sku for that same reason, so
 * they all take the same fallback, and a product without one is reported rather than
 * dropped.
 *
 * Never the sku that was bought: a term has its own code, and reporting that would split
 * one magazine into as many `item_id`s as it sells terms — which is also why the legacy
 * cart, checkout and `purchase` templates all report the base sku, and why an add here and
 * the purchase that follows it join.
 */
function itemId(code: string | null | undefined, productId: number): string {
  return code || String(productId);
}

function item({ productId, baseSkuCode, name, price, index, quantity, coupon, category }: ItemInput): AnalyticsItem {
  const id = itemId(baseSkuCode, productId);

  return {
    id,
    item_id: id,
    item_name: name,
    currency: CURRENCY,
    index: index ?? 1,
    ...(price === undefined ? {} : { price: round2(price) }),
    ...(quantity === undefined ? {} : { quantity }),
    ...(coupon === undefined ? {} : { coupon }),
    ...(category === undefined ? {} : { item_category: category }),
  };
}

/**
 * One product from the storefront api.
 *
 * The price is the base sku's, read the way `SalePrice` draws it — `sale_price || price`,
 * so what is reported is what the visitor is being asked to pay. A coupon is *not* folded
 * in: an offer code's prices are overlaid on the page, and GA4's convention is to report
 * the price before it and name the code in `coupon`. A page quoting something else — a
 * chosen term rather than the base one — passes its own price.
 */
function productItem(product: Product, overrides: Partial<ItemInput> = {}): AnalyticsItem {
  return item({
    productId: product.id,
    baseSkuCode: product.base_sku?.code,
    name: product.name,
    price: product.base_sku?.sale_price || product.base_sku?.price,
    quantity: 1,
    ...overrides,
  });
}

/**
 * One cart line.
 *
 * The price is what one copy costs after its discount. `CartItem.price` is per-copy, not a
 * line total — the api hides this, because asking it for a quantity of three answers with
 * three lines of one, so the two are the same number on every cart V2 has seen — and code
 * that assumes otherwise is right only until a multi-copy line reaches it.
 */
function cartLineItem(line: CartItem, index = 1): AnalyticsItem {
  return item({
    productId: line.product.id,
    baseSkuCode: line.product.base_sku?.code,
    name: line.product.name,
    price: line.price - line.discount,
    quantity: line.quantity || 1,
    index,
  });
}

/**
 * The product a page is about.
 *
 * `value` is the item's own price: one product, one copy.
 */
export function trackViewItem(product: Product, overrides: Partial<ItemInput> = {}): void {
  // A product whose skus have all been deactivated has no base sku: there is no price to
  // report and no view to report it for.
  if (!product.base_sku) {
    return;
  }

  const reported = productItem(product, overrides);

  trackEvent('view_item', {
    currency: CURRENCY,
    value: reported.price,
    items: [reported],
  });
}

/**
 * `add_to_cart` and `remove_from_cart` are the same payload under two names.
 *
 * Both are reported from the cart line the server actually wrote rather than from what the
 * page asked for, so the product, the price and the quantity are the ones in the cart.
 *
 * Wrapped, because this is analytics hanging off a write the customer is waiting on. A tag
 * that throws inside `dataLayer.push` would otherwise reject the add that called it, and
 * the customer would be left with a product in their cart, no confirmation of it, and
 * nothing to say the add worked.
 */
function trackCartEvent(name: string, line: CartItem): void {
  try {
    const reported = cartLineItem(line);

    trackEvent(name, {
      currency: CURRENCY,
      value: round2((reported.price ?? 0) * (reported.quantity ?? 1)),
      items: [reported],
    });
  } catch (failure) {
    console.error(`Could not report ${name}`, failure);
  }
}

/** The line an add produced. */
export function trackAddToCart(line: CartItem): void {
  trackCartEvent('add_to_cart', line);
}

/** The line a removal took away. */
export function trackRemoveFromCart(line: CartItem): void {
  trackCartEvent('remove_from_cart', line);
}

/**
 * The id GA4 reports a list under, derived from its name.
 *
 * The derivation is `_doEcommerceImpression`'s, character for character, so a band that
 * moves from a legacy template to a V2 page keeps its `item_list_id` and its history.
 */
function listId(list: string): string {
  return list
    .toLowerCase()
    .replace(/[^a-z0-9_ ]/g, '')
    .replace(/ +/g, ' ')
    .replace(/ /g, '_');
}

/**
 * The products already reported, and the impressions waiting to go out — both keyed by
 * list, because a page has as many lists as it has bands and each is its own event.
 *
 * Module scope rather than component state, for the reason the dispatcher gives and one
 * more: a tile is unmounted when it scrolls out of a render window and mounted again on
 * the way back, so state kept in the tile would report the same product on every pass.
 */
const reported = new Map<string, Set<string>>();
const batched = new Map<string, AnalyticsItem[]>();
let timer: ReturnType<typeof setTimeout> | null = null;

function seen(list: string): Set<string> {
  let keys = reported.get(list);

  if (!keys) {
    keys = new Set<string>();
    reported.set(list, keys);
  }

  return keys;
}

function flush(): void {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }

  const lists = Array.from(batched.entries());
  batched.clear();

  for (const [list, items] of lists) {
    for (let start = 0; start < items.length; start += MAX_ITEMS_PER_EVENT) {
      trackEvent('view_item_list', {
        item_list_id: listId(list),
        item_list_name: list,
        items: items.slice(start, start + MAX_ITEMS_PER_EVENT),
      });
    }
  }
}

/**
 * A page being left takes its pending batch with it, so the wait above is given up the
 * moment that starts to happen. `visibilitychange` rather than `unload`, which Safari does
 * not fire reliably and which disqualifies a page from the back/forward cache.
 */
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && batched.size) {
      flush();
    }
  });
}

type ImpressionInput = ItemInput & {
  /** `item_list_name`; its id is derived from it. */
  list: string;
  /**
   * Where in the list this showing is, when one list shows the same product more than
   * once — the mega sale's sections are one list and a title can be in several of them.
   * Each showing then counts separately, which is what the legacy page did by tagging the
   * DOM element rather than the product.
   */
  placement?: string;
};

/**
 * Reports a product the visitor has looked at, once per list — batched, so a screenful of
 * tiles is one event.
 *
 * `index` is the position in the list. A caller that knows it passes it; one that does not
 * — a page whose tiles report themselves as they are revealed — gets the order they were
 * reported in, counting from 1.
 */
export function reportImpression({ list, placement, ...input }: ImpressionInput): void {
  const keys = seen(list);
  const key = `${placement ?? ''}|${input.productId}`;

  if (keys.has(key)) {
    return;
  }

  keys.add(key);

  const items = batched.get(list) ?? [];

  items.push(item({ ...input, index: input.index ?? keys.size }));
  batched.set(list, items);

  if (timer === null) {
    timer = setTimeout(flush, BATCH_MS);
  }
}
